/**
 * PostHog + funnel-mirror tracking for GeoSim super-admin toolbar and
 * CTA-por-región admin (production-filterable).
 *
 * Filter in PostHog:
 *   - event starts with `admin_geo_sim_` or `admin_cta_region_`
 *   - property `admin_tool` = geosim | cta_region
 *   - property `site` / `brand` = BRAND_SOURCE_KEY
 *   - property `admin_geo_simulation` = true (simulated visitor sessions)
 */

import { BRAND_SOURCE_KEY, BRAND_SITE_HOST } from "@/lib/brandConfig";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

export type GeoSimAnalyticsProps = Record<string, unknown>;

const BASE_PROPS = (): GeoSimAnalyticsProps => ({
  site: BRAND_SOURCE_KEY,
  brand: BRAND_SOURCE_KEY,
  brand_host: BRAND_SITE_HOST,
  admin_tool: "geosim",
  product_surface: "super_admin_geo_sim",
  environment: import.meta.env.PROD ? "production" : "development",
});

function ph():
  | {
      capture?: (e: string, p?: Record<string, unknown>) => void;
      register?: (p: Record<string, unknown>) => void;
      unregister?: (k: string) => void;
    }
  | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { posthog?: ReturnType<typeof ph> }).posthog as
    | ReturnType<typeof ph>
    | undefined;
}

/** Direct PostHog capture (also mirrored to track-funnel for Slack/DB). */
export function trackGeoSimEvent(
  eventName: string,
  props: GeoSimAnalyticsProps = {},
): void {
  const payload = {
    ...BASE_PROPS(),
    admin_geo_simulation: true,
    ...props,
    href:
      typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
  };

  try {
    ph()?.capture?.(eventName, payload);
  } catch {
    /* non-fatal */
  }

  try {
    mirrorFunnelEvent(
      {
        event_name: eventName,
        props: payload,
      },
      { fireMetaPixel: false },
    );
  } catch {
    /* non-fatal */
  }
}

/** Register session super-properties so ALL subsequent events are tagged while sim is on. */
export function registerGeoSimPersonProps(session: {
  country: string;
  province?: string | null;
  city?: string | null;
  state?: string | null;
  simulateVpn?: string | null;
}): void {
  try {
    ph()?.register?.({
      admin_geo_simulation: true,
      simulated_country: session.country,
      simulated_province: session.province || null,
      simulated_city: session.city || null,
      simulated_state: session.state || null,
      simulated_vpn_mode: session.simulateVpn || "clean",
      site: BRAND_SOURCE_KEY,
      brand: BRAND_SOURCE_KEY,
    });
  } catch {
    /* ignore */
  }
}

export function clearGeoSimPersonProps(): void {
  try {
    const p = ph();
    p?.unregister?.("admin_geo_simulation");
    p?.unregister?.("simulated_country");
    p?.unregister?.("simulated_province");
    p?.unregister?.("simulated_city");
    p?.unregister?.("simulated_state");
    p?.unregister?.("simulated_vpn_mode");
    p?.register?.({ admin_geo_simulation: false });
  } catch {
    /* ignore */
  }
}

export function edgeSnapshotProps(): GeoSimAnalyticsProps {
  try {
    const cfg = getServerTemplateConfig();
    return {
      edge_country: cfg?.country ?? null,
      edge_province: cfg?.province ?? null,
      edge_city: cfg?.city ?? null,
      edge_template: cfg?.templateName ?? null,
      edge_detection_method: cfg?.detectionMethod ?? null,
      edge_cta_from: (cfg as { ctaResolvedFrom?: string } | null)?.ctaResolvedFrom ?? null,
      edge_cta_matched:
        (cfg as { ctaResolvedMatched?: string } | null)?.ctaResolvedMatched ?? null,
      edge_hide_cta: (cfg as { hideCta?: boolean } | null)?.hideCta ?? null,
      edge_admin_geo_simulation:
        (cfg as { adminGeoSimulation?: boolean } | null)?.adminGeoSimulation ?? null,
    };
  } catch {
    return {};
  }
}

// ── Named events (stable for PostHog dashboards / HogQL) ───────────────────

export const GEO_SIM_EVENTS = {
  boot: "admin_geo_sim_boot",
  toolbarOpen: "admin_geo_sim_toolbar_open",
  toolbarClose: "admin_geo_sim_toolbar_close",
  tabChange: "admin_geo_sim_tab_change",
  presetSelect: "admin_geo_sim_preset_select",
  activate: "admin_geo_sim_activate",
  activateError: "admin_geo_sim_activate_error",
  sessionActive: "admin_geo_sim_session_active",
  edgeMismatch: "admin_geo_sim_edge_mismatch",
  exit: "admin_geo_sim_exit",
  debugToggle: "admin_geo_sim_debug_toggle",
  ctaPreview: "admin_geo_sim_cta_preview",
  snapshotCopy: "admin_geo_sim_snapshot_copy",
} as const;

export const CTA_REGION_EVENTS = {
  pageView: "admin_cta_region_page_view",
  save: "admin_cta_region_save",
  saveError: "admin_cta_region_save_error",
  hideGlobalToggle: "admin_cta_region_hide_global_toggle",
  propagationOk: "admin_cta_region_propagation_ok",
  propagationTimeout: "admin_cta_region_propagation_timeout",
} as const;

export function trackCtaRegionEvent(
  eventName: string,
  props: GeoSimAnalyticsProps = {},
): void {
  const payload = {
    site: BRAND_SOURCE_KEY,
    brand: BRAND_SOURCE_KEY,
    brand_host: BRAND_SITE_HOST,
    admin_tool: "cta_region",
    product_surface: "admin_wishlist_config",
    environment: import.meta.env.PROD ? "production" : "development",
    ...props,
    href:
      typeof window !== "undefined" ? window.location.pathname : "/admin/wishlist-config",
  };

  try {
    ph()?.capture?.(eventName, payload);
  } catch {
    /* non-fatal */
  }

  try {
    mirrorFunnelEvent(
      { event_name: eventName, props: payload },
      { fireMetaPixel: false },
    );
  } catch {
    /* non-fatal */
  }
}
